import { Checkbox, Flex, IconButton, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-box
import { Box } from '@aloft/toolkit-sui/chakra';
import { ColumnDef, Row } from '@tanstack/react-table';

import { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { CompSelectionProperty, CompSelectionState } from '../../types.ts';

const SELECT_COLUMN_SIZE = 48;
const AIV_ARV_SELECT_COLUMN_SIZE = 96;

const noFocusRingSx = {
  '& .chakra-checkbox__control:focus': { boxShadow: 'none' },
  '& .chakra-checkbox__control:focus-visible': { boxShadow: 'none' },
};

export const makeSelectionColumn = (
  getRowSelection: () => CompSelectionState,
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
  onHeaderButtonClick?: () => void,
): ColumnDef<CompSelectionProperty> => {
  const FilterButton = () => (
    <IconButton
      icon="filter"
      size="xs"
      variant="secondary"
      ariaLabel="Open filters"
      onClick={() => onHeaderButtonClick?.()}
    />
  );

  if (!isInspectifyOrder || !hasArvOption) {
    return {
      id: 'select',
      size: SELECT_COLUMN_SIZE,
      minSize: SELECT_COLUMN_SIZE,
      maxSize: SELECT_COLUMN_SIZE,
      header: () => (
        <Flex alignItems="center" justifyContent="center" height="100%">
          <FilterButton />
        </Flex>
      ),
      cell: ({ row }: { row: Row<CompSelectionProperty> }) => {
        if (row.id === 'subject') return null;

        const rowId = row.id;
        const rowSelection = getRowSelection();
        const isSelected = !!rowSelection[rowId];

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          const { checked } = e.target;
          setRowSelection((prev) => {
            if (checked) {
              return { ...prev, [rowId]: {} };
            }
            const newSelection = { ...prev };
            delete newSelection[rowId];
            return newSelection;
          });
        };

        return (
          <Flex
            height="100%"
            minWidth={`${SELECT_COLUMN_SIZE}px`}
            alignItems="center"
            justifyContent="center"
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleChange}
                isChecked={isSelected}
                aria-label={`Comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
        );
      },
    };
  }

  return {
    id: 'select',
    size: AIV_ARV_SELECT_COLUMN_SIZE,
    minSize: AIV_ARV_SELECT_COLUMN_SIZE,
    maxSize: AIV_ARV_SELECT_COLUMN_SIZE,
    header: () => (
      <Flex
        alignItems="center"
        justifyContent="space-between"
        height="100%"
        width="100%"
        paddingX="4px"
      >
        <FilterButton />
        <Flex gap="12px">
          <Typography
            variant="body2"
            __dangerDoNotUseSx={{
              color: 'var(--chakra-colors-statusWarning)',
              fontWeight: 700,
              fontSize: '10px',
            }}
          >
            AIV
          </Typography>
          <Typography
            variant="body2"
            __dangerDoNotUseSx={{
              color: 'var(--chakra-colors-statusComplete)',
              fontWeight: 700,
              fontSize: '10px',
            }}
          >
            ARV
          </Typography>
        </Flex>
      </Flex>
    ),
    cell: ({ row }: { row: Row<CompSelectionProperty> }) => {
      if (row.id === 'subject') return null;

      const rowId = row.id;
      const rowSelection = getRowSelection();
      const selection = rowSelection[rowId] || {};
      const isAivSelected = selection.aiv ?? false;
      const isArvSelected = selection.arv ?? false;

      const handleAivChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setRowSelection((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            aiv: checked,
            arv: checked ? false : (prev[rowId]?.arv ?? false),
          },
        }));
      };

      const handleArvChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setRowSelection((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            arv: checked,
            aiv: checked ? false : (prev[rowId]?.aiv ?? false),
          },
        }));
      };

      return (
        <Flex
          height="100%"
          minWidth={`${AIV_ARV_SELECT_COLUMN_SIZE}px`}
          alignItems="center"
          justifyContent="space-around"
          paddingX="8px"
          gap="8px"
          onClick={(e) => e.stopPropagation()}
        >
          <Flex flexDirection="column" alignItems="center" gap="2px">
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleAivChange}
                isChecked={isAivSelected}
                aria-label={`AIV comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
          <Flex flexDirection="column" alignItems="center" gap="2px">
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleArvChange}
                isChecked={isArvSelected}
                aria-label={`ARV comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
        </Flex>
      );
    },
  };
};
